import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { api } from '../../services/api';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import HandymanIcon from '@mui/icons-material/Handyman';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RepeatIcon from '@mui/icons-material/Repeat';
import KPICard from '../../components/common/KPICard';
import { useQuotes, useFollowupQuote, useRejectQuote, useUpdateQuoteNote } from '../../hooks/useApi';
import type { Quote, QuoteType } from '../../types';

const quoteTypeIcons: Record<QuoteType, JSX.Element> = {
  TRANSPORT: <LocalShippingIcon sx={{ fontSize: 14 }} />,
  CONTAINER: <Inventory2Icon sx={{ fontSize: 14 }} />,
  SERVICE: <HandymanIcon sx={{ fontSize: 14 }} />,
  WASTE: <DeleteOutlineIcon sx={{ fontSize: 14 }} />,
  RECURRING: <RepeatIcon sx={{ fontSize: 14 }} />,
};

function daysSince(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
}

function fmtCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('da-DK', { style: 'currency', currency, minimumFractionDigits: 0 }).format(amount);
}

const rejectReasons = [
  { value: 'PRICE', label: 'For dyrt' },
  { value: 'TIME', label: 'Forkert tid' },
  { value: 'LOST_TO_COMPETITOR', label: 'Tabt til konkurrent' },
  { value: 'PROJECT_DROPPED', label: 'Projekt droppet' },
  { value: 'OTHER', label: 'Andet' },
];

export default function QuotePipeline() {
  const navigate = useNavigate();
  const { data: quotes = [] } = useQuotes();
  const followup = useFollowupQuote();
  const reject = useRejectQuote();
  const updateNote = useUpdateQuoteNote();

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; quoteId: string }>({ open: false, quoteId: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [noteDialog, setNoteDialog] = useState<{ open: boolean; quoteId: string; current: string }>({ open: false, quoteId: '', current: '' });

  const grouped = useMemo(() => {
    return {
      DRAFT: quotes.filter(q => q.status === 'DRAFT'),
      SENT: quotes.filter(q => q.status === 'SENT'),
      ACCEPTED: quotes.filter(q => q.status === 'ACCEPTED' || q.status === 'CONVERTED'),
      REJECTED: quotes.filter(q => q.status === 'REJECTED'),
    };
  }, [quotes]);

  // KPIs
  const stats = useMemo(() => {
    const nonDraft = quotes.filter(q => q.status !== 'DRAFT');
    const won = quotes.filter(q => q.status === 'ACCEPTED' || q.status === 'CONVERTED').length;
    const winRate = nonDraft.length > 0 ? (won / nonDraft.length) * 100 : 0;

    // Average days from sent to accepted
    const accepted = quotes.filter(q => q.status === 'ACCEPTED' || q.status === 'CONVERTED');
    const avgDays = accepted.length > 0
      ? accepted.reduce((sum, q) => {
          if (!q.sentAt || !q.acceptedAt) return sum;
          const days = (new Date(q.acceptedAt).getTime() - new Date(q.sentAt).getTime()) / (1000 * 60 * 60 * 24);
          return sum + days;
        }, 0) / accepted.length
      : 0;

    return { winRate, avgDays, pipelineCount: grouped.SENT.length };
  }, [quotes, grouped.SENT.length]);

  async function handleFollowup(id: string) {
    try {
      await followup.mutateAsync(id);
      setSnackbar({ open: true, message: 'Opfølgning sendt', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Fejl', severity: 'error' });
    }
  }

  async function handleReject() {
    try {
      await reject.mutateAsync({ id: rejectDialog.quoteId, reason: rejectReason || null });
      setRejectDialog({ open: false, quoteId: '' });
      setRejectReason('');
      setSnackbar({ open: true, message: 'Tilbud markeret som afvist', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Fejl', severity: 'error' });
    }
  }

  async function handleSaveNote() {
    try {
      await updateNote.mutateAsync({ id: noteDialog.quoteId, note: noteDialog.current || null });
      setNoteDialog({ open: false, quoteId: '', current: '' });
      setSnackbar({ open: true, message: 'Note gemt', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Fejl', severity: 'error' });
    }
  }

  function renderCard(q: Quote) {
    const days = q.sentAt ? daysSince(q.sentAt) : null;
    const ageColor = days != null && days >= 14 ? 'error' : days != null && days >= 7 ? 'warning' : 'primary';
    const typeIcon = quoteTypeIcons[(q.quoteType as QuoteType) || 'TRANSPORT'];
    const followupCount = q.followupCount || 0;

    return (
      <Card
        key={q.id}
        sx={{
          mb: 1,
          borderLeft: '3px solid',
          borderColor: `${ageColor}.main`,
          transition: 'box-shadow 0.15s, transform 0.15s',
          '&:hover': { boxShadow: 3 },
          '& .pipeline-actions': { opacity: 0, transition: 'opacity 0.15s' },
          '&:hover .pipeline-actions': { opacity: 1 },
        }}
      >
        <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
          {/* Top row: # + type icon + age */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ color: 'text.secondary', display: 'flex' }}>{typeIcon}</Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>#{q.quoteNumber}</Typography>
            </Box>
            {days != null && q.status === 'SENT' && (
              <Chip size="small" label={days === 0 ? 'i dag' : `${days} d`} color={ageColor === 'primary' ? 'default' : ageColor} variant="outlined" sx={{ height: 18, fontSize: 10 }} />
            )}
          </Box>

          {/* Customer + amount — clickable area */}
          <Box sx={{ cursor: 'pointer' }} onClick={() => navigate(`/tilbud/${q.id}`)}>
            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>{q.customerName}</Typography>
            {q.description && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {q.description}
              </Typography>
            )}
            <Typography variant="body2" fontWeight={700} color="primary" sx={{ mt: 0.5 }}>
              {fmtCurrency(q.totalAmount, q.currency)}
            </Typography>
          </Box>

          {/* Followup indicator */}
          {followupCount > 0 && (
            <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 0.25 }}>
              ↻ {followupCount} opfølgning{followupCount !== 1 ? 'er' : ''} sendt
            </Typography>
          )}

          {/* Internal note */}
          {q.internalNote && (
            <Box sx={{
              mt: 0.5, px: 0.75, py: 0.25, borderRadius: 0.5,
              bgcolor: 'warning.50', borderLeft: '2px solid', borderColor: 'warning.light',
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                {q.internalNote.length > 70 ? q.internalNote.slice(0, 70) + '...' : q.internalNote}
              </Typography>
            </Box>
          )}

          {/* Quick actions — visible on hover */}
          <Box className="pipeline-actions" sx={{ display: 'flex', gap: 0.5, mt: 0.75, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Tooltip title="Kopier som nyt tilbud">
              <Button
                size="small"
                startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                onClick={async () => {
                  try {
                    const copy = await api.copyQuote(q.id);
                    navigate(`/tilbud/${copy.id}/rediger`);
                  } catch (e) { console.error('Kunne ikke kopiere', e); }
                }}
                sx={{ fontSize: 11, py: 0.25 }}
              >
                Kopier
              </Button>
            </Tooltip>
            {q.status === 'SENT' && (
              <>
                <Tooltip title="Send opfølgnings-email til kunden">
                  <Button size="small" startIcon={<SendIcon sx={{ fontSize: 14 }} />} onClick={() => handleFollowup(q.id)} sx={{ fontSize: 11, py: 0.25 }}>
                    Følg op
                  </Button>
                </Tooltip>
                <Tooltip title="Tilføj intern note">
                  <Button size="small" startIcon={<NoteAltIcon sx={{ fontSize: 14 }} />} onClick={() => setNoteDialog({ open: true, quoteId: q.id, current: q.internalNote || '' })} sx={{ fontSize: 11, py: 0.25 }}>
                    Note
                  </Button>
                </Tooltip>
                <Box sx={{ flex: 1 }} />
                <Button size="small" color="error" onClick={() => setRejectDialog({ open: true, quoteId: q.id })} sx={{ fontSize: 11, py: 0.25 }}>
                  Afvis
                </Button>
              </>
            )}
          </Box>

          {q.status === 'REJECTED' && q.rejectedReason && (
            <Chip size="small" label={rejectReasons.find(r => r.value === q.rejectedReason)?.label || q.rejectedReason} variant="outlined" color="error" sx={{ mt: 0.5, height: 20, fontSize: 10 }} />
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Tilbudspipeline</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => navigate('/tilbud')}>Liste-visning</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/tilbud/ny')}>Nyt tilbud</Button>
        </Box>
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <KPICard title="Win-rate" value={`${stats.winRate.toFixed(0)}%`} color={stats.winRate >= 50 ? 'success' : stats.winRate >= 30 ? 'warning' : 'error'} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KPICard title="Gns. dage til accept" value={`${stats.avgDays.toFixed(1)} d`} color="info" />
        </Grid>
        <Grid item xs={6} md={3}>
          <KPICard title="I pipeline" value={stats.pipelineCount} subtitle="afsendte tilbud" color="primary" />
        </Grid>
        <Grid item xs={6} md={3}>
          <KPICard title="Kladder" value={grouped.DRAFT.length} color="info" />
        </Grid>
      </Grid>

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={3}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Kladder ({grouped.DRAFT.length})
          </Typography>
          {grouped.DRAFT.map(renderCard)}
          {grouped.DRAFT.length === 0 && <Typography variant="caption" color="text.secondary">Ingen</Typography>}
        </Grid>
        <Grid item xs={12} md={3}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Sendt — venter ({grouped.SENT.length})
          </Typography>
          {grouped.SENT.map(renderCard)}
          {grouped.SENT.length === 0 && <Typography variant="caption" color="text.secondary">Ingen</Typography>}
        </Grid>
        <Grid item xs={12} md={3}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Accepteret ({grouped.ACCEPTED.length})
          </Typography>
          {grouped.ACCEPTED.map(renderCard)}
          {grouped.ACCEPTED.length === 0 && <Typography variant="caption" color="text.secondary">Ingen</Typography>}
        </Grid>
        <Grid item xs={12} md={3}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Afvist ({grouped.REJECTED.length})
          </Typography>
          {grouped.REJECTED.map(renderCard)}
          {grouped.REJECTED.length === 0 && <Typography variant="caption" color="text.secondary">Ingen</Typography>}
        </Grid>
      </Grid>

      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, quoteId: '' })} fullWidth maxWidth="xs">
        <DialogTitle>Marker som afvist</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Årsag</InputLabel>
            <Select value={rejectReason} label="Årsag" onChange={(e) => setRejectReason(e.target.value as string)}>
              <MenuItem value="">— Ingen —</MenuItem>
              {rejectReasons.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, quoteId: '' })}>Annuller</Button>
          <Button onClick={handleReject} color="error" variant="contained">Afvis</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={noteDialog.open} onClose={() => setNoteDialog({ open: false, quoteId: '', current: '' })} fullWidth maxWidth="sm">
        <DialogTitle>Intern note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            value={noteDialog.current}
            onChange={(e) => setNoteDialog(s => ({ ...s, current: e.target.value }))}
            placeholder="F.eks. 'Kunden ringede 8/4, vil have rabat på 10%'"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog({ open: false, quoteId: '', current: '' })}>Annuller</Button>
          <Button onClick={handleSaveNote} variant="contained">Gem</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
